import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { toast } from 'react-toastify';
import { useShallow } from 'zustand/react/shallow';

import { useRoomContext } from '@src/contexts';
import SocketService from '@src/services/SocketService';
import { useChatStore, usePeersStore, useRoomConfigStore, useRoomStore } from '@src/stores';

import { EMeetingType, EMessageType, EUserType, IInfoModalContent, IMessage } from '@src/types';
import { EWebViewEvents, sendMessageToAndroid } from '@src/bridge';
import { getMimeType } from '@src/utils';

interface IMessageBubble {
  handleDeleteMessage: (messageId: string) => void;
  handlePinMessage: (messageId: string) => void;
  message: IMessage;
}

const EDIT_WINDOW_MS = 10 * 60 * 1000;

const useMessageBubble = ({ handleDeleteMessage, handlePinMessage, message }: IMessageBubble) => {
  const [isFocused, setIsFocused] = useState(false);
  const [openActionsDrawer, setOpenActionsDrawer] = useState(false);
  const [withinTimeframe, setWithinTimeframe] = useState(true);

  const bubbleRef = useRef<HTMLDivElement | null>(null);

  const {
    peer_msg,
    timestamp,
    originalTimestamp,
    peer_name,
    isPinned,
    peer_id,
    chatMessageId,
    file,
    edited,
    peer_record_id,
    metadata,
  } = message;

  const {
    currentPeer,
    isHostOrCohost,
    currentPeerType,
    isMobileDevice,
    participantsPeers,
    peerAction,
    isWebinarAttendee,
  } = useRoomContext();

  const {
    selectedReceiverData,
    setSelectedReceiverData,
    setEditingMessageId,
    setEditingInitialText,
    editingMessageId,
    bannedUserIds,
    addBannedUser,
  } = useChatStore(
    useShallow((state) => ({
      selectedReceiverData: state.selectedReceiverData,
      setSelectedReceiverData: state.setSelectedReceiverData,
      setEditingMessageId: state.setEditingMessageId,
      setEditingInitialText: state.setEditingInitialText,
      editingMessageId: state.editingMessageId,
      bannedUserIds: state.bannedUserIds,
      addBannedUser: state.addBannedUser,
    }))
  );

  const { setInfoModalContent, setIsInfoModalOpen, meetingType } = useRoomStore(
    useShallow((state) => ({
      setIsInfoModalOpen: state.setIsInfoModalOpen,
      setInfoModalContent: state.setInfoModalContent,
      meetingType: state.meetingType,
    }))
  );

  const { isBanButtonEnabled, isPrivateChatEnabled } = useRoomConfigStore(
    useShallow((state) => ({
      isBanButtonEnabled: state.chatConfig.chatBanButton,
      isPrivateChatEnabled: state.chatConfig.privateChatButton,
    }))
  );

  const isMyMessage = useMemo(
    () => currentPeer?.peer_info?.peer_id === peer_id || currentPeer?.record_id === peer_record_id,
    [currentPeer?.peer_info?.peer_id, currentPeer?.record_id, peer_id, peer_record_id]
  );

  const isCalculatorMessage = metadata?.source === 'calculator';

  const canEdit = useMemo(
    () => Boolean(isMyMessage && withinTimeframe && peer_msg && chatMessageId && !isCalculatorMessage),
    [isMyMessage, withinTimeframe, peer_msg, chatMessageId, isCalculatorMessage]
  );

  const canDeleteAttachment = isMyMessage || (isHostOrCohost && message.type === EMessageType.PUBLIC);

  const isEditingBubble = useMemo(
    () => Boolean(chatMessageId && editingMessageId === chatMessageId),
    [chatMessageId, editingMessageId]
  );

  const fileHandlers = useMemo(
    () => ({
      view: () => {
        if (!file?.fileUrl) return;
        window.open(file.fileUrl, '_blank');
      },
      download: async () => {
        try {
          if (!file?.fileUrl) return;
          const url = file.fileUrl;
          sendMessageToAndroid({
            type: EWebViewEvents.DOWNLOAD_FILE,
            payload: {
              id: 'meet-attached-file',
              fileUrl: file.fileUrl,
              fileName: file.fileName,
              mimeType: getMimeType(file.fileExtension.slice(1)),
              fileSize: file.fileSize,
            },
          });
          if (url.startsWith('blob:') || url.startsWith('data:')) {
            const a = document.createElement('a');
            a.href = url;
            a.download = file.fileName || 'download';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            return;
          }
          const response = await fetch(url);
          const blob = await response.blob();
          const objectUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = objectUrl;
          a.download = file.fileName || 'download';
          document.body.appendChild(a);
          a.click();

          document.body.removeChild(a);
          window.URL.revokeObjectURL(objectUrl);
        } catch (err) {
          console.error('Download failed:', err);
        }
      },
    }),
    [file]
  );

  const handleCopyMessage = useCallback((msg: string) => {
    navigator.clipboard.writeText(msg);
    toast.success('Message copied successfully!', {
      position: 'top-left',
      className: 'toast toast-black',
    });
  }, []);

  const speechText = useCallback((msg: string) => {
    const speech = new SpeechSynthesisUtterance();
    speech.text = msg;
    speech.rate = 0.9;
    window.speechSynthesis.speak(speech);
  }, []);

  const toggleDrawer = () => {
    setOpenActionsDrawer((prev) => !prev);
  };

  // >>> New: start editing in bottom composer
  const startEditing = useCallback(() => {
    if (!canEdit || !chatMessageId) return;
    setIsFocused(false);
    setOpenActionsDrawer(false);
    setEditingMessageId(chatMessageId);
    setEditingInitialText(peer_msg || '');
    // focus bottom composer
    requestAnimationFrame(() => {
      document.getElementById('chat-composer-textarea')?.focus();
    });
  }, [canEdit, chatMessageId, peer_msg, setEditingInitialText, setEditingMessageId]);
  const canBanUser = useMemo(() => {
    // Prevent self-banning
    if (isMyMessage || peer_id === SocketService.getSocketId()) {
      return false;
    }

    const isCurrentUserCoHost = currentPeerType === EUserType.CO_HOST;
    if (isCurrentUserCoHost) {
      return false;
    }

    return (
      isBanButtonEnabled &&
      !bannedUserIds.has(peer_id) &&
      (EMeetingType.MEETING === meetingType
        ? !!participantsPeers.find((peer) => peer.peer_info.peer_id === peer_id)
        : true)
    );
  }, [isBanButtonEnabled, isMyMessage, peer_id, bannedUserIds, meetingType, participantsPeers, currentPeerType]);
  const handleBanOnly = useCallback(() => {
    peerAction({ from_peer_name: 'me', id: peer_id, action: 'ban', emit: true });
    addBannedUser(peer_id);
    setIsInfoModalOpen(false);
  }, [peer_id, peerAction, addBannedUser, setIsInfoModalOpen]);

  const handleBanAndDelete = useCallback(() => {
    peerAction({
      from_peer_name: 'me',
      id: peer_id,
      action: 'ban',
      emit: true,
      broadcast: true,
      extraFields: { deleteMessages: true },
    });
    addBannedUser(peer_id);
    setIsInfoModalOpen(false);
  }, [peer_id, peerAction, addBannedUser, setIsInfoModalOpen]);

  const handleCancelBan = useCallback(() => {
    setIsInfoModalOpen(false);
  }, [setIsInfoModalOpen]);

  const banModalConfig = useMemo<IInfoModalContent>(() => {
    const isWebinar = meetingType === EMeetingType.WEBINAR;

    if (isWebinar) {
      return {
        title: 'Ban User',
        description: 'Are you sure you want to ban this attendee from the meeting?',
        buttons: [
          {
            text: 'Ban Only',
            onClick: handleBanOnly,
            variant: 'secondary',
            grayOutlined: true,
          },
          {
            text: 'Ban & Delete Messages',
            onClick: handleBanAndDelete,
            variant: 'primary',
          },
        ],
      };
    }

    return {
      title: 'Ban User',
      description: 'Are you sure you want to ban this attendee from the meeting?',
      buttons: [
        {
          text: 'Ban',
          onClick: handleBanOnly,
          variant: 'primary',
        },
        {
          text: 'Cancel',
          onClick: handleCancelBan,
          variant: 'secondary',
          grayOutlined: true,
        },
      ],
    };
  }, [meetingType, handleBanOnly, handleBanAndDelete, handleCancelBan]);

  const handleBanUser = useCallback(() => {
    setInfoModalContent(banModalConfig);
    setIsInfoModalOpen(true);
  }, [banModalConfig, setInfoModalContent, setIsInfoModalOpen]);

  // keep actions for bottom sheet
  const messageActions = useMemo(() => {
    return [
      { label: 'Edit', action: startEditing, enable: canEdit },
      {
        label: 'Delete',
        action: () => handleDeleteMessage(chatMessageId || ''),
        enable: isMyMessage || (isHostOrCohost && selectedReceiverData.key === EMessageType.PUBLIC),
      },
      { label: 'Copy', action: () => handleCopyMessage(peer_msg) },
      { label: 'Speech', action: () => speechText(peer_msg) },
      {
        label: isPinned ? 'Unpin Message' : 'Pin Message',
        action: () => handlePinMessage(chatMessageId || ''),
        enable: !isWebinarAttendee,
      },
      {
        label: 'Reply Privately',
        action: () => setSelectedReceiverData({ key: EMessageType.PRIVATE, value: peer_id }),
        enable:
          isPrivateChatEnabled &&
          peer_id !== SocketService.getSocketId() &&
          !!participantsPeers.find((peer) => peer.peer_info.peer_id === peer_id) &&
          selectedReceiverData.key !== EMessageType.PRIVATE,
      },
      {
        label: 'Ban User',
        action: handleBanUser,
        enable: canBanUser,
      },
    ].filter((action) => action.enable !== false);
  }, [
    startEditing,
    canEdit,
    isMyMessage,
    isHostOrCohost,
    selectedReceiverData.key,
    isPinned,
    isWebinarAttendee,
    isPrivateChatEnabled,
    peer_id,
    participantsPeers,
    handleBanUser,
    canBanUser,
    handleDeleteMessage,
    chatMessageId,
    handleCopyMessage,
    peer_msg,
    speechText,
    handlePinMessage,
    setSelectedReceiverData,
  ]);

  useEffect(() => {
    if (isEditingBubble) {
      setIsFocused(true);
      bubbleRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    } else {
      setIsFocused(false);
    }
  }, [isEditingBubble]);
  useEffect(() => {
    const displayTimestamp = originalTimestamp || timestamp;
    const sentAt = new Date(displayTimestamp).getTime();
    const expiresAt = sentAt + EDIT_WINDOW_MS;
    const msLeft = expiresAt - Date.now();

    if (msLeft <= 0) {
      setWithinTimeframe(false);
      return;
    }
    const timer = setTimeout(() => setWithinTimeframe(false), msLeft);
    return () => clearTimeout(timer);
  }, [originalTimestamp, timestamp]);

  return {
    //message:
    file,
    edited,
    peer_id,
    peer_msg,
    isPinned,
    peer_name,
    metadata,
    timestamp,
    originalTimestamp,

    setIsFocused,
    toggleDrawer,
    messageActions,
    openActionsDrawer,

    bubbleRef,
    isFocused,
    fileHandlers,
    isMobileDevice,
    isEditingBubble,
    canDeleteAttachment,
  };
};

export default useMessageBubble;
